import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';

// UI
import { BlogPostCard } from '@virginexperiencedays/components-v2/src/cards/BlogPostCard';
import { Breadcrumbs } from '@virginexperiencedays/components-v2/src/navigation/Breadcrumbs';
import { Divider } from '@virginexperiencedays/components-v2/src/layout/Divider';
import { Newsletter } from '@virginexperiencedays/components-v2/src/layout/Newsletter';
import { Pagination } from '@virginexperiencedays/components-v2/src/layout/Pagination';
import { Content } from '@virginexperiencedays/components-v2/src/cms/Content';
import { PostHeroPanel } from '@virginexperiencedays/components-v2/src/pages/blog/PostHeroPanel';
import { Grid } from '@virginexperiencedays/components-v2/src/layout/Grid';
import { EditorsPickRenderer as EditorsPick } from './search/ProductCard';

// types
import type { BlogCategoryPageType, BlogPostPageType } from '@virginexperiencedays/cms';
import type { ProductMapType } from '@virginexperiencedays/products';

// SEO
import blogListingAuthorPageStructuredData from '../SEO/StructuredData/blogListingAuthorPage';
import SEO from '../SEO/SEO';
import { GLOBAL_SETTINGS } from '../tracking/gtm/consts';

//  utils
import DynamicYield from '../DynamicYield/DynamicYield';
import { DyContextPageType } from '@virginexperiencedays/dy';
import { fallbackImage } from '../../utils/fallbackImage';
import { SIGNUP_SOURCE, submit } from '../../utils/newsletter';
import { buildMetaRobotsContent } from '../SEO/utils/buildMetaRobotsContent';

// hooks
import { useTracking } from '../tracking/useTracking';
import { pageView as trackFbPageView } from '../tracking/fb/events';

type BlogCategoryPageProps = {
  category: BlogCategoryPageType;
  categoryPosts: BlogPostPageType[];
  commonCrumbs: { name: string; path?: string }[];
  products: ProductMapType;
  currentPage: number;
  totalPosts: number;
  postsPerPage: number;
  isLandingPage: boolean;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

function BlogCategoryPage({
  category,
  categoryPosts,
  commonCrumbs,
  products,
  currentPage,
  totalPosts,
  postsPerPage,
  isLandingPage,
  hasNextPage,
  hasPrevPage,
}: BlogCategoryPageProps) {
  const router = useRouter();
  const pathname = router.pathname;

  useTracking({
    legacy: { pageType: 'Blog Listing' },
    gtm: { initial: [GLOBAL_SETTINGS, { ecommerce: null }] },
    fb: () => {
      trackFbPageView({
        name: category.title,
      });
    },
  });

  return (
    <div>
      <Head>
        <link rel="preconnect" href="https://images.prismic.io/" />
        {process.env.NEXT_PUBLIC_HOME_LINK ? (
          currentPage === 1 ? (
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${category._meta?.uid}`}
            />
          ) : (
            <link
              rel="canonical"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${category._meta?.uid}?page=${currentPage}`}
            />
          )
        ) : null}
        {hasPrevPage &&
          (currentPage === 2 ? (
            <link
              rel="prev"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${category._meta?.uid}`}
            />
          ) : (
            <link
              rel="prev"
              href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${
                category._meta?.uid
              }?page=${currentPage - 1}`}
            />
          ))}
        {hasNextPage && (
          <link
            rel="next"
            href={`${process.env.NEXT_PUBLIC_HOME_LINK}/experience-blog/${
              category._meta?.uid
            }?page=${currentPage + 1}`}
          />
        )}
      </Head>
      <SEO
        {...category}
        og_image={category.og_image ?? fallbackImage.OpenGraph}
        meta_title={
          currentPage === 1
            ? category.meta_title ?? `${category.title} | Virgin Experience Days`
            : category.meta_title ??
              `${category.title} - Page ${currentPage} | Virgin Experience Days`
        }
        meta_description={
          category.meta_description ??
          `Discover the ${category.title} range from Virgin Experience Days, packed with helpful tips, advice, and inspiration.`
        }
        structuredData={blogListingAuthorPageStructuredData({
          name: category.title,
          urlSlug: category._meta?.uid,
          baseUrl: `experience-blog`,
          totalPosts,
          currentPage,
          postsPerPage,
          breadcrumbs: commonCrumbs
            .concat([
              {
                name: `${category.title}`,
                path: `/experience-blog/${category._meta?.uid}`,
              },
            ])
            .filter((crumb) => crumb.name)
            .map((crumb) => ({ name: crumb.name, url: crumb.path })),
          abstract: category.intro_copy
            .filter((intro_copy) => intro_copy.text)
            .map((intro_copy) => intro_copy.text)
            .join('\n'),
          collectionItems: categoryPosts
            ? categoryPosts
                .flat()
                .filter((item) => item.title)
                .map((item) => ({
                  name: item.title,
                  url: `/experience-blog/${item._meta?.uid}`,
                }))
            : null,
        })}
        meta_robots={buildMetaRobotsContent(category?.meta_robots)}
      />
      <DynamicYield type={DyContextPageType.OTHER} data={pathname ?? []} />
      <Content className="py-8">
        <Breadcrumbs
          crumbs={commonCrumbs.concat([
            {
              name: `${category?.title}`,
            },
          ])}
        />
        {category.title && (
          <PostHeroPanel
            className="mb-10"
            title={category.title}
            subDescription={
              isLandingPage
                ? category.intro_copy
                    .filter((intro_copy) => intro_copy.text)
                    .map((intro_copy) => intro_copy.text)
                : null
            }
          />
        )}
        <Grid
          className="md:gap-y-20"
          space="lg"
          columns={{
            mobile: 1,
            largeMobile: 2,
            tablet: 3,
          }}
        >
          {categoryPosts &&
            categoryPosts.map((post, index) => (
              <BlogPostCard SEOHeading="h2" key={index} post={post} href={post._meta?.uid} />
            ))}
        </Grid>
        {categoryPosts && (
          <Pagination
            wrapperClassName="my-12 md:my-20"
            className="my-12 md:my-20"
            itemsCount={totalPosts}
            perPage={postsPerPage}
            currentPage={currentPage}
            renderLink={(page) =>
              page === 1 || !page
                ? `/experience-blog/${category._meta?.uid}`
                : `/experience-blog/${category._meta?.uid}?page=${page}`
            }
          />
        )}
      </Content>
      <Content className="py-8">
        <EditorsPick
          trackClassName="children:md:w-[calc(25%_-_12px)] children:lg:w-[calc(25%_-_12px)] children:xl:w-[calc(25%_-_12px)] lg:max-w-full"
          cardImageClassName="aspect-[18/13]"
          title="Editor's Picks"
          description={category.editors_picks_description
            .filter((description) => description.text)
            .map((description) => description.text)}
          productsList={category.editors_picks
            .filter((editors_pick) => editors_pick.product_id)
            ?.map(({ product_id }) => {
              return { ...products[product_id] };
            })
            .filter(
              (product) =>
                product.title &&
                product.url &&
                product.media?.mainImage &&
                product.status === 'active'
            )
            .slice(0, 4)}
        />
      </Content>
      <Content className="py-8">
        <Divider variant="grey" />
        <Newsletter
          onSubmit={submit}
          signUpSource={SIGNUP_SOURCE.blogCategory}
          className="mb-10"
          roundedLeftButton={false}
        />
      </Content>
    </div>
  );
}

export default BlogCategoryPage;
